import React, { useState, useEffect } from "react";
import "../styles/ReportePagosYPopularidad.css";
import { IP } from '../configuracion.js';

const BASE_API_URL = `${IP}/tavpos/backend`;

const ReportePagosYPopularidad = ({ property = "propiedad2", activeReport }) => {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState({
    reachable: null,
    testResponse: null,
  });
  const [numberFormat, setNumberFormat] = useState('en-US'); // Default fallback
  const [currency, setCurrency] = useState('USD'); // Default fallback

  const buildApiUrl = (endpoint) =>
    `${BASE_API_URL}/${endpoint}?property=${encodeURIComponent(property)}`;

  const checkApiEndpoint = async () => {
    const url = buildApiUrl("lectura_pagos.php");
    try {
      console.log("Checking API endpoint:", url);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Property": property,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });
      console.log("checkApiEndpoint response status:", response.status);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      console.log("checkApiEndpoint response data:", data);
      setApiStatus({ reachable: true, testResponse: data });
      return true;
    } catch (error) {
      console.error("checkApiEndpoint error:", error.message);
      setApiStatus({ reachable: false, testResponse: null });
      return false;
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(buildApiUrl("get_timezone.php"));
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al cargar configuración (${response.status}): ${errorText}`);
      }
      const data = await response.json();
      console.log("Datos de get_timezone.php:", JSON.stringify(data, null, 2));
      if (!data.success || !data.data) {
        throw new Error(data.message || "No se pudo obtener la configuración.");
      }
      setNumberFormat(data.data.numberFormat || 'en-US');
      setCurrency(data.data.currency || 'USD');
      console.log("Configuración establecida - numberFormat:", data.data.numberFormat, "currency:", data.data.currency);
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      setError(error.message || "Error al cargar configuración. Usando en-US y USD como fallback.");
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat(numberFormat, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const fetchPagos = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("Fetching pagos...");
      const isApiReachable = await checkApiEndpoint();
      console.log("isApiReachable:", isApiReachable);
      if (!isApiReachable) {
        throw new Error("API no accesible. Verifica tu conexión de red.");
      }

      const pagosResponse = await fetch(buildApiUrl("lectura_pagos.php"), {
        method: "GET",
        headers: {
          "X-Property": property,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });
      console.log("fetchPagos response status:", pagosResponse.status);
      if (!pagosResponse.ok) throw new Error(`Error ${pagosResponse.status}`);

      const pagosData = await pagosResponse.json();
      console.log("fetchPagos response data:", pagosData);
      if (!pagosData.success)
        throw new Error(pagosData.message || "Error en respuesta de pagos");

      setPagos(pagosData.data || []);
    } catch (err) {
      console.error("fetchPagos error:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig().then(() => fetchPagos());
  }, [property]);

  const ApiStatusIndicator = () => (
    <div className={`api-status ${apiStatus.reachable === false ? "error" : ""}`}>
      <p>
        <strong>Estado del API:</strong>{" "}
        {apiStatus.reachable === false ? " ❌ Inaccesible" : " ⌛ Verificando..."}
      </p>
    </div>
  );

  if (loading)
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <p>Cargando reportes...</p>
      </div>
    );

  if (error)
    return (
      <div className="error-message">
        <div className="error-header">
          <i className="bi bi-exclamation-triangle"></i>
          <h3>Error al cargar datos</h3>
        </div>
        <p>{error}</p>
        <button className="btn-retry" onClick={fetchPagos}>
          <i className="bi bi-arrow-repeat"></i> Reintentar
        </button>
      </div>
    );

  return (
    <div className="reporte-container">
      <h2>
        <i className="bi bi-credit-card"></i> Resumen de Pagos
        <small className="property-badge">{property}</small>
      </h2>

      {apiStatus.reachable !== true && <ApiStatusIndicator />}

      <div className="seccion-pagos">
        <h3>
          <i className="bi bi-credit-card"></i> Resumen de Pagos
        </h3>

        {pagos.length > 0 ? (
          <>
            <div className="resumen-pagos">
              {Object.entries(
                pagos.reduce((acc, pago) => {
                  const key = pago.nombre_pago || "Sin nombre";
                  if (!acc[key]) {
                    acc[key] = { total: 0, propina: 0, count: 0 };
                  }
                  acc[key].total += pago.monto_pagado || 0;
                  acc[key].propina += pago.propina || 0;
                  acc[key].count++;
                  return acc;
                }, {})
              ).map(([nombre, datos]) => (
                <div key={nombre} className="metodo-pago">
                  <div className="metodo-header">
                    <span className="metodo-nombre">{nombre}</span>
                    <span className="metodo-count">
                      {datos.count} transacciones
                    </span>
                  </div>
                  <div className="metodo-detalle">
                    <span>
                      <i className="bi bi-cash-stack"></i>{" "}
                      {formatCurrency(datos.total)}
                    </span>
                    <span>
                      <i className="bi bi-coin"></i>{" "}
                      {formatCurrency(datos.propina)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="detalle-pagos">
              <h4>
                <i className="bi bi-list-ul"></i> Detalle de Transacciones
              </h4>
              <div className="transacciones-grid">
                {pagos.map((pago, index) => (
                  <div key={index} className="transaccion-card">
                    <div className="transaccion-header">
                      <span className="transaccion-metodo">
                        {pago.nombre_pago || "Sin nombre"}
                      </span>
                      <span className="transaccion-fecha">
                        {pago.fecha_pago
                          ? new Date(pago.fecha_pago).toLocaleString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="transaccion-detalle">
                      <span>
                        <i className="bi bi-cash"></i>{" "}
                        {formatCurrency(pago.monto_pagado || 0)}
                      </span>
                      <span>
                        <i className="bi bi-gift"></i>{" "}
                        {formatCurrency(pago.propina || 0)}
                      </span>
                      <span>
                        <i className="bi bi-calendar-check"></i>{" "}
                        {pago.fecha_control
                          ? new Date(pago.fecha_control).toLocaleString()
                          : "N/A"}
                      </span>
                      <span>
                        <i className="bi bi-table"></i> Mesa: {pago.mesa || "N/A"}
                      </span>
                      <span>
                        <i className="bi bi-person"></i> Cliente:{" "}
                        {pago.cliente || "N/A"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="no-data">
            <i className="bi bi-database-exclamation"></i>
            <p>No hay datos de pagos disponibles</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportePagosYPopularidad;